import {API_BASE_URL} from '../../../services/authApi';
import {FeedAuthor} from '../../../services/socialFeedApi';

export const isRequestCanceled = (error: unknown) => (
  error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')
);

export const formatFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export const optimizePostImageFile = async (
  file: File,
  maxBytes: number,
): Promise<File> => {
  if (file.size <= maxBytes) {
    return file;
  }

  const loadedImage = await loadImageFile(file);
  const preferredMimeType = file.type === 'image/png' ? 'image/webp' : file.type || 'image/jpeg';
  const outputMimeType = preferredMimeType === 'image/webp' || preferredMimeType === 'image/jpeg'
    ? preferredMimeType
    : 'image/jpeg';
  const extension = outputMimeType === 'image/webp' ? 'webp' : 'jpg';

  let width = loadedImage.naturalWidth || loadedImage.width;
  let height = loadedImage.naturalHeight || loadedImage.height;
  let scale = Math.min(1, 2560 / Math.max(width, height));
  let smallestCandidate: File | null = null;

  for (let pass = 0; pass < 6; pass += 1) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Your browser could not prepare this image for upload.');
    }

    context.drawImage(loadedImage, 0, 0, canvas.width, canvas.height);

    for (const quality of [0.92, 0.82, 0.72, 0.62, 0.52, 0.42]) {
      const blob = await canvasToBlob(canvas, outputMimeType, quality);
      const candidateFile = new File(
        [blob],
        replaceFileExtension(file.name, extension),
        {type: blob.type, lastModified: Date.now()},
      );

      if (!smallestCandidate || candidateFile.size < smallestCandidate.size) {
        smallestCandidate = candidateFile;
      }

      if (candidateFile.size <= maxBytes) {
        return candidateFile;
      }
    }

    scale *= 0.8;
  }

  if (smallestCandidate && smallestCandidate.size < file.size) {
    return smallestCandidate;
  }

  throw new Error('This image is too large to optimize for upload. Please choose a smaller file.');
};

export const formatRelativeTime = (dateString: string) => {
  const now = new Date().getTime();
  const date = new Date(dateString).getTime();

  if (Number.isNaN(date)) {
    return 'Recently';
  }

  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds <= 60) return 'Just now';

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
};

type FeedEntityWithAuthor = {
  authorId?: FeedAuthor | null;
};

export const getAuthorName = (post: FeedEntityWithAuthor) => post.authorId?.profile?.name?.trim() || 'User';

export const getDisplayInitial = (name?: string) => name?.trim().charAt(0).toUpperCase() || 'U';

export const getAuthorInitial = (post: FeedEntityWithAuthor) => getDisplayInitial(getAuthorName(post));

export const getProfilePath = (userId?: string | null) => {
  const normalizedUserId = userId?.trim();

  if (!normalizedUserId) {
    return null;
  }

  return `/profile?userId=${encodeURIComponent(normalizedUserId)}`;
};

export const getAssetUrl = (url?: string) => {
  if (!url) {
    return '';
  }

  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:') ||
    url.startsWith('blob:')
  ) {
    return url;
  }

  if (API_BASE_URL.startsWith('http://') || API_BASE_URL.startsWith('https://')) {
    try {
      return new URL(url, API_BASE_URL).toString();
    } catch (error) {
      return url;
    }
  }

  return url;
};

export const formatViewCount = (viewCount: number) => {
  if (viewCount >= 1000000) {
    return `${(viewCount / 1000000).toFixed(viewCount >= 10000000 ? 0 : 1)}M`;
  }

  if (viewCount >= 1000) {
    return `${(viewCount / 1000).toFixed(viewCount >= 10000 ? 0 : 1)}K`;
  }

  return `${viewCount}`;
};

export const formatDuration = (durationSeconds?: number) => {
  if (!durationSeconds || durationSeconds <= 0) {
    return '--:--';
  }

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const readVideoDurationFromPreviewUrl = (previewUrl: string) => new Promise<number | undefined>((resolve) => {
  const previewVideo = document.createElement('video');
  previewVideo.preload = 'metadata';
  previewVideo.muted = true;

  const cleanup = () => {
    previewVideo.removeAttribute('src');
    previewVideo.load();
  };

  previewVideo.onloadedmetadata = () => {
    const durationSeconds = Number.isFinite(previewVideo.duration) && previewVideo.duration > 0
      ? Math.round(previewVideo.duration)
      : undefined;

    cleanup();
    resolve(durationSeconds);
  };

  previewVideo.onerror = () => {
    cleanup();
    resolve(undefined);
  };

  previewVideo.src = previewUrl;
});

export const createVideoThumbnailPreviewUrl = async (
  previewUrl: string,
  preferredCaptureTimeSeconds: number = 1,
) => {
  if (!previewUrl) {
    throw new Error('A video preview URL is required to generate a thumbnail.');
  }

  return new Promise<string>((resolve, reject) => {
    const previewVideo = document.createElement('video');
    let settled = false;
    let timeoutId = 0;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      previewVideo.pause();
      previewVideo.removeAttribute('src');
      previewVideo.load();
    };

    const settleResolve = (thumbnailPreviewUrl: string) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      resolve(thumbnailPreviewUrl);
    };

    const settleReject = (error: Error) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      reject(error);
    };

    const drawCurrentFrame = async () => {
      const naturalWidth = previewVideo.videoWidth;
      const naturalHeight = previewVideo.videoHeight;

      if (!naturalWidth || !naturalHeight) {
        settleReject(new Error('Unable to prepare a reel thumbnail preview.'));
        return;
      }

      const maxDimension = 720;
      const scale = Math.min(1, maxDimension / Math.max(naturalWidth, naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(naturalHeight * scale));

      const context = canvas.getContext('2d');

      if (!context) {
        settleReject(new Error('Your browser could not prepare this reel thumbnail.'));
        return;
      }

      context.drawImage(previewVideo, 0, 0, canvas.width, canvas.height);

      try {
        const thumbnailBlob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
        settleResolve(URL.createObjectURL(thumbnailBlob));
      } catch (error) {
        settleReject(error instanceof Error ? error : new Error('Unable to prepare this reel thumbnail.'));
      }
    };

    const captureFromNearestFrame = () => {
      if (previewVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        void drawCurrentFrame();
        return;
      }

      previewVideo.addEventListener('loadeddata', () => {
        void drawCurrentFrame();
      }, {once: true});
    };

    previewVideo.preload = 'auto';
    previewVideo.muted = true;
    previewVideo.playsInline = true;
    previewVideo.onerror = () => {
      settleReject(new Error('Unable to read the selected reel preview.'));
    };

    previewVideo.addEventListener('loadedmetadata', () => {
      const durationSeconds = Number.isFinite(previewVideo.duration) && previewVideo.duration > 0
        ? previewVideo.duration
        : 0;
      const targetTime = durationSeconds > 0
        ? Math.min(preferredCaptureTimeSeconds, Math.max(durationSeconds - 0.1, 0))
        : 0;

      if (targetTime <= 0.05) {
        captureFromNearestFrame();
        return;
      }

      previewVideo.addEventListener('seeked', () => {
        void drawCurrentFrame();
      }, {once: true});

      try {
        previewVideo.currentTime = targetTime;
      } catch (error) {
        captureFromNearestFrame();
      }
    }, {once: true});

    timeoutId = window.setTimeout(() => {
      captureFromNearestFrame();
    }, 5000);

    previewVideo.src = previewUrl;
    previewVideo.load();
  });
};

const loadImageFile = (file: File) => new Promise<HTMLImageElement>((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to read the selected image file.'));
  };

  image.src = objectUrl;
});

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Unable to prepare the selected image for upload.'));
      return;
    }

    resolve(blob);
  }, mimeType, quality);
});

const replaceFileExtension = (fileName: string, nextExtension: string) => {
  const normalizedFileName = fileName.trim() || `upload.${nextExtension}`;
  const fileNameWithoutExtension = normalizedFileName.replace(/\.[^.]+$/, '');
  return `${fileNameWithoutExtension}.${nextExtension}`;
};
